/**
 * Llamadas HTTP del subdominio **Precios del cotizador de puertas**.
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`,
 * Pr. 2): ningún `fetch`/`ky` suelto. Cubre `GET`/`PUT /api/v1/cotizador/precios`
 * (`application/api/domains/cotizador.py:285-306`).
 *
 * - Permiso backend: `all` (no basta `use_cotizador`).
 * - **Sin sede**: los precios son globales (un JSON en disco); estos endpoints NO
 *   usan `X-Sede-Id` (lo inyecta el cliente, pero el backend lo ignora aquí).
 * - El **PUT es un patch con merge**: el backend solo toca las claves enviadas y
 *   preserva el resto del JSON (`reglas_cobro`, troqueladas…). Se envían las 18
 *   claves del form juntas; NO se envía `success` ni claves ajenas.
 */
import { api } from "@/lib/api";

import type { PreciosConfig, UpdatePreciosBody } from "./precios.types";

/**
 * Lee la configuración de precios (`GET /cotizador/precios`).
 *
 * Permiso backend: `all`. Devuelve las 18 claves de la lista blanca + `success`.
 */
export async function fetchPrecios(): Promise<PreciosConfig> {
  const response = await api.get("cotizador/precios");
  return (await response.json()) as PreciosConfig;
}

/**
 * Respuesta de éxito del PUT (`guardar_precios`): no devuelve el objeto
 * actualizado, solo confirma. El estado nuevo se obtiene re-GET (invalidación).
 */
export interface UpdatePreciosResult {
  success: boolean;
  message: string;
}

/**
 * Guarda la configuración de precios (`PUT /cotizador/precios`).
 *
 * Permiso backend: `all`. Cuerpo = las 18 claves del form (sin `success`); el
 * backend hace merge sobre el JSON en disco. La validación numérica se hace en el
 * cliente ANTES de llamar (el backend revienta con 500 ante `NaN`/vacío).
 */
export async function updatePrecios(
  body: UpdatePreciosBody,
): Promise<UpdatePreciosResult> {
  const response = await api.put("cotizador/precios", { json: body });
  return (await response.json()) as UpdatePreciosResult;
}
